"use client";
import { KeepProblemsActiveControls } from "./KeepProblemsControls";

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { usePathname } from "next/navigation";
import ProblemAnswerFetcher from "@/components/ProblemAnswerFetcher";
import RandomizedProblems from "@/components/RandomizedProblems";
import ProblemRenderDiagnosticsBoundary from "@/components/ProblemRenderDiagnosticsBoundary";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RotateCcw,
  Filter,
  Eye,
  EyeOff,
  Pin,
  SlidersHorizontal,
} from "lucide-react";

import { outlineToggleClass } from "@/lib/quiz-control-styles";
import { isConfirmDismissed } from "@/lib/dismissible-confirm";
import { ChoicesVisibilityProvider, useChoicesVisibility } from "@/components/ChoicesVisibilityContext";
import {
  MobileBottomToolRegistration,
  type MobileBottomToolItem,
} from "@/components/mobile-bottom-tools";
import * as Sentry from "@sentry/nextjs";
import { useUser } from "@clerk/nextjs";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { getAttemptedProblemIds, getAttemptedProblemIdsBySessionId } from "@/lib/server-actions/problem/history";
import { getAnonymousSessionId } from "@/lib/anonymousSession";
import { clearPastExamCompletionCache } from "@/hooks/usePastExamCompletion";
import { useGamjaExamChallenge } from "@/hooks/useGamjaExamChallenge";
import {
  GamjaExamChallengeResult,
  GamjaExamChallengeSummary,
} from "@/components/gamja-exam/GamjaExamChallenge";
import {
  clearKeptRandomProblemIds,
  clearProblemAnswerDraftsForPath,
  clearProblemAnswerSessionStatesForPath,
  getKeptRandomProblemIdsState,
  getRandomProblemsKeepMode,
  refreshProblemAnswerDraftsForPath,
  refreshProblemAnswerSessionStatesForPath,
  saveKeptRandomProblemIds,
  saveRandomProblemsKeepMode,
} from "@/lib/random-problems-persistence";
import {
  createRandomProblemsRequestGate,
  randomProblemsSessionRegistry,
} from "@/lib/random-problems-session";
import type { QuizClass } from "@/lib/quiz-problems";

interface ScopeConfig {
  scope: string;
  count: number;
}

interface RandomProblemsProps {
  // 모바일 점수 패널에 표시할 시험명. 기출 회차명보다 우선한다.
  scoreTitle?: string;
  scopes?: ScopeConfig[];
  // 하위 호환성을 위한 scope prop (단일 값 또는 배열)
  scope?: string | ScopeConfig[];
  count?: number;
  // past prop: {2025: 2} 형태로 examYear와 examRound 지정
  past?: Record<number, number>;
  // tags prop: bare 대분류는 모든 중분류를 포함하고, 대분류>중분류는 정확히 매칭
  tags?: string[];
  // tagMode: "all"은 모든 태그 포함, "any"는 하나라도 포함 (기본: 단일태그=all, 복수태그=any)
  tagMode?: "all" | "any";
  // language: 문제 ID prefix로 언어 필터링 (예: "c", "java", "python")
  language?: string;
  // 매운감자(고난도) 전용 퀴즈 트리거(JCG-3260). "상"은 "상 이상"(서버 풀=상+극상)을 의미하며
  // 서버에서 Premium 이상 권한을 다시 검증한다.
  minDifficulty?: "상";
  // minDifficulty와 함께 사용할 출제 영역. 기존 scopes/tags/past 경로와 분리한다.
  quizClass?: QuizClass;
  // premium: true이면 프리미엄 문제만 출제 풀에서 추출 (JCG-1430)
  // 프리미엄 모의고사 단일 페이지에서 사용. 기존 scopes/tags/past/language와 모두 조합 가능
  premium?: boolean;
  // final: true이면 파이널 모의고사 풀(_final_) 문제만 추출 (JCG-1675)
  // /final-mock-exam 페이지 전용. scopes와 조합하여 과목별 랜덤 출제
  final?: boolean;
  // finalSession: 파이널 회차 스코프 (JCG-2897, 예: "2026-1"·"2026-2"). 지정 시 그 회차 문제만.
  // final=true와 함께 사용. 미지정이면 전체 파이널 풀(하위호환).
  finalSession?: string | null;
  // hardcore: true이면 하드코어 11 회차 풀(_hardcore_) 문제만 추출 (JCG-1564)
  // /hardcore-11 페이지 전용. scopes(언어별 문제 수)·hardcoreSession(회차, 필수)과 함께 사용.
  // 서버가 Premium 이상 권한을 다시 검증한다.
  hardcore?: boolean;
  hardcoreSession?: string | null;
  // unsolvedOnly 모드에서 primary scopes 풀이 부족할 때 추가로 조회할 scope 목록 (JCG-1638)
  // 예: 이론 퀴즈는 가중치 scope 분포(primary) + 같은 class의 전체 scope(fallback)
  fallbackScopes?: ScopeConfig[];
  // 감자퀴즈에서만 기초문제 제외 설정을 제공한다.
  allowExcludeBasic?: boolean;
  // 명시적인 "다시 풀기"에서 부모가 출제 범위를 다시 준비해야 할 때 호출한다.
  // 콜백이 있으면 새 props가 반영된 뒤 effect가 조회하므로 현재 scopes로 즉시 요청하지 않는다.
  onRetry?: () => void;
}

// 부모 컴포넌트에서 ref로 호출 가능한 imperative API
export interface RandomProblemsHandle {
  /**
   * localStorage 비우고 새 sampling 실행 (JCG-1430)
   * - 페이월 차감 없이 호출
   * - 현재 pathname의 캐시된 문제 ID 목록 제거 후 재출제
   */
  reset: () => void;
}

// localStorage 유틸리티 함수
const getStorageKey = (pathname: string): string => {
  return `random-problems-${pathname}`;
};

const saveProblemIdsToStorage = (pathname: string, problemIds: string[]): void => {
  try {
    const key = getStorageKey(pathname);
    localStorage.setItem(key, JSON.stringify(problemIds));
  } catch (error) {
    console.error("localStorage 저장 실패:", error);
  }
};

const getProblemIdsFromStorage = (pathname: string): string[] | null => {
  try {
    const key = getStorageKey(pathname);
    const stored = localStorage.getItem(key);
    if (stored) {
      // 레거시/변조 localStorage 가 비배열(또는 비문자열 원소)이면 복원 경로 크래시를 막는다(JCG-2485).
      const parsed: unknown = JSON.parse(stored);
      if (!Array.isArray(parsed)) return null;
      const ids = parsed.filter((v): v is string => typeof v === "string");
      return ids.length > 0 ? ids : null;
    }
  } catch (error) {
    console.error("localStorage 조회 실패:", error);
  }
  return null;
};

// iOS Safari 백그라운드 복귀 리로드 복원용 마커 (JCG-2895).
// iOS Safari 는 백그라운드 탭을 메모리 확보로 폐기했다가 복귀 시 페이지를 전체 리로드한다. 전체
// 리로드는 모듈을 새로 평가하므로 세션 레지스트리가 비어 "새로고침 시 새 문제"(JCG-283) 경로로
// 빠져 직전에 풀던 세트를 버리고 1번부터 다시 출제한다. 전체 리로드 후엔 능동 F5 와 Safari 자동
// 리로드를 "리로드가 일어났다"는 사실만으로는 구분할 수 없다(둘 다 언로드 직전 visibilitychange:hidden
// 이 발생). 그래서 "탭이 얼마나 오래 백그라운드에 있다가 다시 로드됐는지"(away-duration)로 판정한다:
//   - 능동 F5: 언로드→재로드가 거의 즉시라 away 가 매우 짧다(< MIN_AWAY_MS) → 새 문제(JCG-283 보존).
//   - 백그라운드 복귀: 자리를 비운 시간(초~분)만큼 away 가 길다([MIN, MAX]) → 직전 세트 복원.
// 탭이 hidden 될 때 시각을 기록하고, 다시 visible 로 "리로드 없이" 복귀하면 마커를 지운다(이중 안전장치).
// 마커는 마운트 시 소비 즉시 삭제(one-shot)해 복귀 직후 의도적 F5 에는 새 문제가 나오도록 한다.
const HIDDEN_AT_STORAGE_PREFIX = "random-problems-hidden-at-";
// away 가 이 값보다 짧으면 능동 F5(언로드→즉시 재로드)로 보고 복원하지 않는다.
// 10초: 느린 네트워크에서 능동 F5 재로드가 몇 초 걸려도 새 문제가 나오게 여유를 둔다(백그라운드
// 복귀는 보통 이보다 훨씬 오래 자리를 비우므로 복원 손실은 거의 없다).
const MIN_AWAY_MS = 10 * 1000; // 10초
// away 가 이 값보다 길면 너무 오래 자리를 비운 것으로 보고 복원하지 않는다(새 문제).
// 30분은 "같은 학습 세션"으로 볼 상한일 뿐이다. 복원되면 직전 문제 세트(problemIds)와 함께
// 입력한 답/풀이 상태(didBackgroundRestore → persistDraftAcrossReload)와 셔플 표시 순서(JCG-3510)도 되살린다.
const MAX_AWAY_MS = 30 * 60 * 1000; // 30분

const getHiddenAtStorageKey = (pathname: string): string =>
  `${HIDDEN_AT_STORAGE_PREFIX}${pathname}`;

const markPathnameHidden = (pathname: string): void => {
  try {
    localStorage.setItem(getHiddenAtStorageKey(pathname), String(Date.now()));
  } catch {
    // localStorage 접근 불가 시 무시
  }
};

const clearPathnameHidden = (pathname: string): void => {
  try {
    localStorage.removeItem(getHiddenAtStorageKey(pathname));
  } catch {
    // localStorage 접근 불가 시 무시
  }
};

// 마지막으로 탭이 hidden 된 뒤 지금까지 경과한 ms. 마커가 없거나 손상이면 null.
const getAwayMsSinceHidden = (pathname: string): number | null => {
  try {
    const raw = localStorage.getItem(getHiddenAtStorageKey(pathname));
    if (!raw) return null;
    const hiddenAt = Number(raw);
    if (!Number.isFinite(hiddenAt)) return null;
    return Date.now() - hiddenAt;
  } catch {
    return null;
  }
};

/**
 * 네트워크/서버 에러 시 재시도하는 fetch wrapper
 * 5xx 에러 또는 네트워크 오류 시 최대 maxRetries까지 재시도
 */
const fetchWithRetry = async (
  url: string,
  options?: RequestInit,
  maxRetries = 2
): Promise<Response> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    options?.signal?.throwIfAborted();
    try {
      const response = await fetch(url, options);
      if (response.status >= 500 && attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        options?.signal?.throwIfAborted();
        continue;
      }
      return response;
    } catch (err) {
      if (options?.signal?.aborted) {
        throw err;
      }
      if (attempt >= maxRetries) {
        throw err;
      }
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
  throw new Error('Fetch failed after retries');
};

const UNSOLVED_ONLY_STORAGE_KEY = 'quiz-unsolved-only';
const EXCLUDE_BASIC_STORAGE_KEY = 'quiz-exclude-basic';
// "다시 풀기" 확인 다이얼로그 "다시 보지 않기" 저장 키 (JCG-2527). JCG-2510 reveal 패턴 미러링. prefix 관례: jcg:
const RETRY_CONFIRM_DISMISSED_KEY = "jcg:retry-confirm:dismissed";

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const getRemainingKeepMs = (expiresAt: number): number => Math.max(0, expiresAt - Date.now());

const getRemainingKeepMsForPath = (pathname: string): number | null => {
  const keptState = getKeptRandomProblemIdsState(pathname);
  return keptState === null ? null : getRemainingKeepMs(keptState.expiresAt);
};

/**
 * 문제 보기 설정 사이드바 컨트롤 (JCG-2066) — "안 푼 문제만" + "전체 보기 숨기기".
 *
 * RandomProblems 가 로딩·풀이·모든 문제 풀이 완료·결과 없음·오류 등 **모든 상태에서 지속적으로**
 * 렌더한다. RandomizedProblems(문제 풀이) 안의 ChoicesToggleBar 에 의존하면 종료 상태에서
 * 컨트롤이 사라지고 버튼이 본문으로 밀려나므로, 컨트롤을 셸 라이프사이클에서 분리해 항상 유지한다.
 * - 셸(데스크탑 글 페이지): quizControlsSlot(우측 필기 위)에 카드로 portal.
 * - 비셸(모바일): 기존처럼 본문 상단 인라인 바.
 *
 * 전체 보기 토글은 ChoicesVisibilityContext 를 읽으므로, RandomProblems 가 ChoicesVisibilityProvider
 * 로 전체 출력을 감싸야 한다(RandomizedProblems 는 부모 provider 를 재사용).
 */
const SidebarFilterControl: React.FC<{
  filterContent: React.ReactNode;
  showChoicesToggle: boolean;
}> = ({
  filterContent,
  showChoicesToggle,
}) => {
  const { inShell, quizControlsSlot, setControlsActive } = useArticleShellSlots();
  const choices = useChoicesVisibility();

  // 셸이면 슬롯을 켜둔다(scoreActive 가 false 인 종료 상태에서도 슬롯이 렌더되도록).
  useEffect(() => {
    if (!inShell) return;
    setControlsActive(true);
    return () => setControlsActive(false);
  }, [inShell, setControlsActive]);

  // 전체 보기 숨기기 토글 (프리미엄 컨텍스트가 있을 때만)
  const toggleButton = showChoicesToggle && choices ? (
    <Button
      onClick={choices.toggleGlobalChoicesHidden}
      variant="outline"
      size="sm"
      aria-pressed={choices.globalChoicesHidden}
      className={outlineToggleClass(choices.globalChoicesHidden)}
    >
      {choices.globalChoicesHidden ? (
        <>
          <Eye className="h-4 w-4 mr-1" aria-hidden="true" />
          전체 보기 보이기
        </>
      ) : (
        <>
          <EyeOff className="h-4 w-4 mr-1" aria-hidden="true" />
          전체 보기 숨기기
        </>
      )}
    </Button>
  ) : null;

  if (inShell && quizControlsSlot) {
    return createPortal(
      <div className="@container/quiz-filters flex flex-col gap-2 rounded-xl border border-border bg-card p-3 [&>button]:w-full">
        {filterContent}
        {toggleButton}
      </div>,
      quizControlsSlot,
    );
  }
  // 비셸(모바일/태블릿)은 하단 Dock 도구가 담당한다.
  return null;
};

interface RandomProblemsMobileToolsProps {
  canKeepProblems: boolean;
  keepProblemsEnabled: boolean;
  isKeepProblemsActive: boolean;
  problemIdsLength: number;
  pathname: string;
  isPremiumOrAbove: boolean;
  unsolvedOnly: boolean;
  allowExcludeBasic: boolean;
  excludeBasic: boolean;
  onKeepProblemsToggle: (checked: boolean) => void;
  onKeepProblemsExtend: () => number | null;
  onUnsolvedOnlyToggle: (checked: boolean) => void;
  onExcludeBasicToggle: (checked: boolean) => void;
}

const RandomProblemsMobileTools = React.memo(function RandomProblemsMobileTools({
  canKeepProblems,
  keepProblemsEnabled,
  isKeepProblemsActive,
  problemIdsLength,
  pathname,
  isPremiumOrAbove,
  unsolvedOnly,
  allowExcludeBasic,
  excludeBasic,
  onKeepProblemsToggle,
  onKeepProblemsExtend,
  onUnsolvedOnlyToggle,
  onExcludeBasicToggle,
}: RandomProblemsMobileToolsProps) {
  const choices = useChoicesVisibility();
  const { inShell } = useArticleShellSlots();

  const settingsTool = React.useMemo<MobileBottomToolItem | null>(
    () => {
      const canToggleChoices = Boolean(choices && isPremiumOrAbove);
      const activeSettingCount = [
        isPremiumOrAbove && unsolvedOnly,
        allowExcludeBasic && excludeBasic,
        canToggleChoices && choices?.globalChoicesHidden,
        canKeepProblems && keepProblemsEnabled,
      ].filter(Boolean).length;

      if (!isPremiumOrAbove && !canToggleChoices && !canKeepProblems && !allowExcludeBasic) {
        return null;
      }

      return {
        id: "random-problems-settings",
        kind: "status-panel",
        label: "문제설정",
        icon: SlidersHorizontal,
        order: 6,
        status: activeSettingCount > 0 ? `${activeSettingCount}개` : undefined,
        ariaLabel:
          activeSettingCount > 0
            ? `문제 보기 설정 열기, 적용 중인 설정 ${activeSettingCount}개`
            : "문제 보기 설정 열기",
        sheet: {
          title: "문제 보기 설정",
          attached: true,
          content: (
            <div className="space-y-2">
              {(isPremiumOrAbove || allowExcludeBasic) && (
                <div className={`grid gap-2 ${isPremiumOrAbove && allowExcludeBasic ? "grid-cols-2" : "grid-cols-1"}`}>
                  {isPremiumOrAbove && (
                    <Button
                      onClick={() => onUnsolvedOnlyToggle(!unsolvedOnly)}
                      variant="outline"
                      size="sm"
                      aria-pressed={unsolvedOnly}
                      className={`${outlineToggleClass(unsolvedOnly)} min-w-0 w-full justify-center px-1`}
                    >
                      <Filter className="mr-1 h-4 w-4 shrink-0" aria-hidden="true" />
                      안 푼 문제만
                    </Button>
                  )}
                  {allowExcludeBasic && (
                    <Button
                      onClick={() => onExcludeBasicToggle(!excludeBasic)}
                      variant="outline"
                      size="sm"
                      aria-pressed={excludeBasic}
                      className={`${outlineToggleClass(excludeBasic)} min-w-0 w-full justify-center px-1`}
                    >
                      <Filter className="mr-1 h-4 w-4 shrink-0" aria-hidden="true" />
                      기초문제 제외
                    </Button>
                  )}
                </div>
              )}
              {canToggleChoices && choices && (
                <Button
                  onClick={choices.toggleGlobalChoicesHidden}
                  variant="outline"
                  size="sm"
                  aria-pressed={choices.globalChoicesHidden}
                  className={`${outlineToggleClass(choices.globalChoicesHidden)} w-full justify-center`}
                >
                  {choices.globalChoicesHidden ? (
                    <>
                      <Eye className="h-4 w-4 mr-1" aria-hidden="true" />
                      전체 보기 보이기
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-4 w-4 mr-1" aria-hidden="true" />
                      전체 보기 숨기기
                    </>
                  )}
                </Button>
              )}
              {canKeepProblems && (
                <div className="space-y-1 rounded-lg border border-border bg-muted/40 p-1">
                  <Button
                    onClick={() => onKeepProblemsToggle(!keepProblemsEnabled)}
                    variant="outline"
                    size="sm"
                    aria-pressed={keepProblemsEnabled}
                    className={`${outlineToggleClass(keepProblemsEnabled)} w-full justify-center`}
                  >
                    <Pin className="h-4 w-4 mr-1" aria-hidden="true" />
                    문제 유지
                  </Button>
                  {isKeepProblemsActive && (
                    <KeepProblemsActiveControls
                      key={pathname}
                      readRemaining={() => getRemainingKeepMsForPath(pathname)}
                      hasProblemIds={problemIdsLength > 0}
                      onExtend={onKeepProblemsExtend}
                    />
                  )}
                </div>
              )}
            </div>
          ),
        },
      };
    },
    [
      canKeepProblems,
      allowExcludeBasic,
      choices,
      excludeBasic,
      isKeepProblemsActive,
      isPremiumOrAbove,
      keepProblemsEnabled,
      onKeepProblemsExtend,
      onKeepProblemsToggle,
      onUnsolvedOnlyToggle,
      onExcludeBasicToggle,
      pathname,
      problemIdsLength,
      unsolvedOnly,
    ],
  );

  if (inShell) return null;

  return (
    settingsTool ? <MobileBottomToolRegistration tool={settingsTool} /> : null
  );
});

const RandomProblems = React.forwardRef<RandomProblemsHandle, RandomProblemsProps>(({
  scoreTitle,
  scopes: scopesProp,
  scope,
  count = 10,
  past,
  tags,
  tagMode,
  language,
  minDifficulty,
  quizClass,
  premium,
  final,
  finalSession,
  hardcore,
  hardcoreSession,
  fallbackScopes,
  allowExcludeBasic = false,
  onRetry,
}, ref) => {
  const pathname = usePathname();
  // 파이널 회차별로 "선택된 문제 세트" 저장 네임스페이스를 분리한다(JCG-2897 후속).
  // getStorageKey/세션 레지스트리가 pathname 고정이면 회차 토글(key remount)이나 리마운트 복원 시
  // 직전 회차의 세트가 서로 복원돼 섞인다. final+finalSession 일 때만 `#final=` 접미사로 갈라주고,
  // 그 외 모든 페이지는 storagePath === pathname 이라 기존 동작을 100% 보존한다(no-op).
  // ※ 문제 초안/세션상태 캐시는 자식 ProblemAnswer 가 usePathname 으로 쓰므로 pathname 그대로 유지한다.
  // 하드코어 11(JCG-1564)도 같은 이유로 회차별 `#hardcore=` 접미사로 분리한다.
  const storagePath = React.useMemo(() => {
    if (final && finalSession) return `${pathname}#final=${finalSession}`;
    if (hardcore && hardcoreSession) return `${pathname}#hardcore=${hardcoreSession}`;
    return pathname;
  }, [final, finalSession, hardcore, hardcoreSession, pathname]);
  const [requestGate] = React.useState(createRandomProblemsRequestGate);
  const routeSnapshot = React.useSyncExternalStore(
    randomProblemsSessionRegistry.subscribe,
    () => randomProblemsSessionRegistry.getRouteSnapshot(pathname),
    () => "0:inactive",
  );
  const [routeRevision, routeActivity] = routeSnapshot.split(":");
  const isRouteActive = routeActivity === "active";
  const handledRouteRevisionRef = useRef(routeRevision);

  // LayoutSwitch의 라우트 commit 통지로 이탈 즉시 요청을 취소한다(JCG-3044).
  // 언마운트가 늦는 Next page 트리도 처리하고, 실제 언마운트는 cleanup 폴백으로 취소한다.
  useEffect(() => {
    const unregisterRouteExit = randomProblemsSessionRegistry.registerRouteExit(
      pathname,
      requestGate.abort,
    );
    return () => {
      unregisterRouteExit();
      requestGate.abort();
    };
  }, [pathname, requestGate]);

  const { isSignedIn, isLoaded: isUserLoaded } = useUser();
  const {
    isPremiumOrAbove,
    isAdFree,
    isLoaded: isPassLoaded,
  } = useEffectivePass();
  
  // past prop이 있으면 examYear와 examRound 추출
  const pastConfig = React.useMemo(() => {
    if (!past) return null;
    const entries = Object.entries(past);
    if (entries.length === 0) return null;
    // 첫 번째 항목만 사용 (한 번에 하나의 연도/회차만 지원)
    const [examYear, examRound] = entries[0];
    return {
      examYear: parseInt(examYear, 10),
      examRound: parseInt(examRound.toString(), 10)
    };
  }, [past]);

  // 과거회차 풀이 페이지를 떠날 때(제출 후 그리드 복귀) 클라이언트 완주 캐시를 비워
  // 다음 그리드 로드에서 즉시 최신 완주값을 표시한다(JCG-2242 #1). past 모드에서만.
  // deps는 객체(pastConfig)가 아닌 안정적인 boolean으로 — 부모 리렌더로 past prop이 매번
  // 새 객체가 되어도 effect가 재실행돼 clear가 남발되지 않도록 한다(언마운트 시에만 clear).
  const isPastMode = pastConfig !== null;
  const examScoreTitle =
    scoreTitle ??
    (pastConfig
      ? `정보처리기사 ${pastConfig.examYear}년 ${pastConfig.examRound}회`
      : undefined);
  useEffect(() => {
    if (!isPastMode) return;
    return () => clearPastExamCompletionCache();
  }, [isPastMode]);

  // 감자시험 페이지면 세트를 모두 채점할 때 도전 1회를 기록한다(JCG-3865). 기출·파이널 풀이는 제외.
  const gamjaExam = useGamjaExamChallenge({
    pathname,
    storagePath,
    enabled: !isPastMode && !final,
  });
  const { onSetStarted: onGamjaExamSetStarted } = gamjaExam;

  // scopes prop을 우선 사용, 없으면 scope prop을 변환 (past가 없을 때만)
  const scopes: ScopeConfig[] = React.useMemo(() => {
    // past prop이 있으면 scopes 로직 무시
    if (pastConfig) {
      return [];
    }
    
    if (scopesProp) {
      return scopesProp;
    }
    
    if (scope) {
      // scope가 배열인 경우 (ScopeConfig[])
      if (Array.isArray(scope)) {
        return scope;
      }
      // scope가 단일 문자열인 경우
      return [{ scope: scope as string, count }];
    }
    
    return [];
  }, [scopesProp, scope, count, pastConfig]);
  
  const [problemIds, setProblemIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allSolved, setAllSolved] = useState(false);
  const [filterNotice, setFilterNotice] = useState<string | null>(null);
  const [keepProblemsEnabled, setKeepProblemsEnabled] = useState(false);
  const [hasLoadedKeepMode, setHasLoadedKeepMode] = useState(false);
  // iOS Safari 백그라운드 복귀 복원(JCG-2895)이 이번 마운트에서 발동했는지. true 면 복원된 세트가
  // 직전과 동일하므로 입력한 답(draft)/풀이 상태도 유지·복원되도록 persistDraftAcrossReload 를 켠다
  // (문제유지 keep 과 동일 경로 재사용). 능동 F5(새 문제) 경로에서는 false 로 남아 기존대로 답이 초기화된다.
  const [didBackgroundRestore, setDidBackgroundRestore] = useState(false);
  // "다시 풀기" 확인 다이얼로그 상태 (JCG-2527). 버튼 오클릭으로 풀이 기록이 초기화되는 불편 해소.
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);
  // 자식(RandomizedProblems)이 보고한 "화면 표시 순서(셔플 반영)"의 동기 미러. state 는 한 렌더 늦게
  // 반영돼, 셔플 완료 직후 문제유지 토글 시 직전 셔플 순서를 놓치고 원본 순서를 잠그는 레이스가 났다
  // → ref 로 즉시 반영해 토글 시점에 실제 표시 순서를 그대로 잠근다(JCG-2310 리뷰).
  const displayedProblemIdsRef = useRef<string[]>([]);
  const canKeepProblems = isPassLoaded && isAdFree;
  const isKeepProblemsActive = keepProblemsEnabled && canKeepProblems;
  // 리로드 너머 복원 여부의 SSOT — keep(유료 1시간) 또는 백그라운드 복귀 복원(JCG-2895)이면 true.
  // 본문 draft(persistDraftAcrossReload)와 좌측 목록/점수(restoreProblemResultsAcrossReload)는
  // 반드시 같은 조건이어야 하므로(비대칭 시 목록만 미풀이로 어긋남, JCG-3222) 한 곳에서 파생한다.
  // 셔플 표시 순서(restoreOrderAcrossReload)도 같은 조건으로 복원한다(JCG-3510). keep 은 셔플하지 않아 무관하다.
  const restoreDraftAndResults = isKeepProblemsActive || didBackgroundRestore;
  const hasFetched = useRef(false);
  // 최초 마운트의 첫 effect 실행 여부 — 리사이즈로 LayoutSwitch 가 셸↔클래식을 교체하면
  // RandomProblems 가 리마운트되는데, 이때 localStorage 에 저장된 직전 세트를 그대로 복원해
  // 새 문제 재추첨을 막는다(JCG-2251). "다시 풀기"/reset 은 마운트 이후 트리거되므로 이 ref 가
  // 이미 소진된 상태라 정상적으로 새 세트를 fetch 한다.
  const isInitialMount = useRef(true);

  // 설정 복원과 권한 검사를 한곳에서 처리한다. null이면 아직 저장값을 읽기 전이다(JCG-3215).
  const [storedUnsolvedOnly, setStoredUnsolvedOnly] = useState<boolean | null>(null);
  const unsolvedOnly = isPassLoaded && isPremiumOrAbove && storedUnsolvedOnly === true;
  const [storedExcludeBasic, setStoredExcludeBasic] = useState<boolean | null>(null);
  const excludeBasic = allowExcludeBasic && storedExcludeBasic === true;
  const appliedUnsolvedOnlyRef = useRef<boolean | null>(null);
  const appliedExcludeBasicRef = useRef<boolean | null>(null);
  const discardFilteredProblemsRef = useRef(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setKeepProblemsEnabled(getRandomProblemsKeepMode());
      setHasLoadedKeepMode(true);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, []);

  // Clerk 로딩 중에는 Premium의 저장값을 지우지 않는다. 권한이 확인된 뒤에만 복원/정리한다.
  useEffect(() => {
    if (!isPassLoaded) return;
    let nextUnsolvedOnly = false;
    try {
      if (isPremiumOrAbove) {
        nextUnsolvedOnly = localStorage.getItem(UNSOLVED_ONLY_STORAGE_KEY) === 'true';
      } else {
        // StrictMode의 effect 재실행에서도 첫 번째로 발견한 만료 설정을 보존한다.
        discardFilteredProblemsRef.current ||= localStorage.getItem(UNSOLVED_ONLY_STORAGE_KEY) === 'true';
        localStorage.setItem(UNSOLVED_ONLY_STORAGE_KEY, 'false');
      }
    } catch {
      // localStorage 접근 불가 시 필터를 끈 상태로 출제
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStoredUnsolvedOnly(nextUnsolvedOnly);
  }, [isPassLoaded, isPremiumOrAbove]);

  useEffect(() => {
    let enabled = false;
    if (allowExcludeBasic) {
      try {
        enabled = localStorage.getItem(EXCLUDE_BASIC_STORAGE_KEY) === 'true';
      } catch {
        // localStorage 접근 불가 시 기본값 사용
      }
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStoredExcludeBasic(enabled);
  }, [allowExcludeBasic]);

  // 안 푼 문제 필터용: 시도한 문제 ID 조회
  const fetchAttemptedIds = React.useCallback(async (): Promise<string[]> => {
    try {
      if (isSignedIn) {
        const result = await getAttemptedProblemIds();
        return result.success ? result.problemIds : [];
      } else {
        const sessionId = getAnonymousSessionId();
        if (!sessionId) return [];
        const result = await getAttemptedProblemIdsBySessionId(sessionId);
        return result.success ? result.problemIds : [];
      }
    } catch {
      return [];
    }
  }, [isSignedIn]);

  // fetchProblems 함수를 useCallback으로 분리
  const fetchProblems = React.useCallback(async () => {
      const requestController = requestGate.start();
      const isCurrentRequest = () => requestGate.isActive(requestController);
      const finishCurrentRequest = () => {
        if (!isCurrentRequest()) return;
        setIsLoading(false);
        hasFetched.current = true;
        requestGate.finish(requestController);
      };

      try {
        setIsLoading(true);
        setError(null);
        setAllSolved(false);
        setFilterNotice(null);
        if (!isKeepProblemsActive) {
          clearProblemAnswerDraftsForPath(pathname);
          clearProblemAnswerSessionStatesForPath(pathname);
        }

        // 안 푼 문제만 필터링: 시도한 문제 ID 조회
        let attemptedIds: string[] = [];
        if (unsolvedOnly) {
          attemptedIds = await fetchAttemptedIds();
          if (!isCurrentRequest()) return;
          if (process.env.NODE_ENV === 'development') {
            console.log('[안 푼 문제만] 시도한 문제 수:', attemptedIds.length);
            console.log('[안 푼 문제만] 시도한 문제 ID:', attemptedIds);
          }
        }

        // past prop이 있으면 examYear와 examRound로 조회
        if (pastConfig) {
          const cacheBuster = `${Date.now()}_${Math.random()}`;
          const premiumParam = premium ? '&premium=true' : '';
          const apiUrl = `/api/random-problems?examYear=${pastConfig.examYear}&examRound=${pastConfig.examRound}${premiumParam}&cacheBuster=${cacheBuster}`;

          try {
            const response = await fetchWithRetry(apiUrl, {
              cache: 'no-store',
              signal: requestController.signal,
            });

            if (!response.ok) {
              throw new Error(`API 요청 실패: ${response.status}`);
            }

            const result = await response.json();
            if (!isCurrentRequest()) return;

            if (result.success) {
              let ids = result.problemIds as string[];
              // 안 푼 문제만 필터링 (past는 API에서 excludeIds 무시하므로 클라이언트 필터)
              if (unsolvedOnly && attemptedIds.length > 0) {
                const beforeCount = ids.length;
                const attemptedSet = new Set(attemptedIds);
                ids = ids.filter(id => !attemptedSet.has(id));
                if (process.env.NODE_ENV === 'development') {
                  console.log(`[안 푼 문제만] past 필터: ${beforeCount}개 → ${ids.length}개 (${beforeCount - ids.length}개 제외)`);
                }
                if (ids.length === 0) {
                  setAllSolved(true);
                }
              }
              setProblemIds(ids);
              saveProblemIdsToStorage(storagePath, ids);
              if (isKeepProblemsActive) {
                saveKeptRandomProblemIds(storagePath, ids);
              }
              randomProblemsSessionRegistry.markFetched(storagePath);
            } else {
              Sentry.captureMessage("과거 시험 문제 조회 실패", {
                level: "warning",
                tags: {
                  component: "RandomProblems",
                  examYear: pastConfig.examYear.toString(),
                  examRound: pastConfig.examRound.toString()
                },
                extra: {
                  pathname,
                  error: result.error,
                  pastConfig
                }
              });
              setError(`문제 조회 실패: ${result.error}`);
            }
          } catch (err) {
            if (!isCurrentRequest()) return;
            setError("문제 목록을 불러오는 중 오류가 발생했습니다.");
            console.error("문제 조회 실패:", err);
          } finally {
            finishCurrentRequest();
          }
          return;
        }

        // 이전 문제 ID 목록 조회
        const previousProblemIds = getProblemIdsFromStorage(storagePath);

        // excludeIds 구성 (JCG-2080)
        // - 일반 모드: 직전에 보여준 문제(previousProblemIds)를 제외해 재출제 시 새 문제를 노출.
        // - 안 푼 문제만 모드: previousProblemIds 를 제외하지 '않는다'. "안 푼" 판정은 attempted 기준이어야 하며,
        //   풀이 작은 파이널/모의고사에서 직전 20문제를 함께 제외하면 후보가 0이 되어 allSolved("모두 풀었습니다!")로
        //   오판된다(파이널 풀은 작아 previousProblemIds 만으로 풀이 비워짐). 따라서 attempted 만으로 필터.
        // 하드코어 11(JCG-1564): 회차 풀 = 출제 수(11)라 직전 세트를 빼도 서버가 같은 11문제로 다시 채운다.
        // 헛선택·긴 URL을 막기 위해 직전 세트는 보내지 않고, 안 푼 문제만 모드는 이번 회차 문제의 시도 기록만 보낸다.
        const hardcoreIdToken = hardcore && hardcoreSession ? `_hardcore_${hardcoreSession}_` : null;
        const allExcludeIds = hardcoreIdToken
          ? unsolvedOnly
            ? [...attemptedIds].filter((id) => id.includes(hardcoreIdToken))
            : []
          : unsolvedOnly
            ? [...attemptedIds]
            : [...(previousProblemIds || [])];

        const cacheBuster = `${Date.now()}_${Math.random()}`;
        const premiumParam = premium ? '&premium=true' : '';
        const finalParam = final ? '&final=true' : '';
        // 파이널 회차 스코프 (JCG-2897) — final 모드에서만 유효, 지정 시 그 회차만 출제
        const finalSessionParam = final && finalSession ? `&finalSession=${encodeURIComponent(finalSession)}` : '';
        // 하드코어 11 회차 풀 (JCG-1564) — 회차 필수. scopes 분기로만 조회한다.
        const hardcoreParam = hardcore && hardcoreSession
          ? `&hardcore=true&hardcoreSession=${encodeURIComponent(hardcoreSession)}`
          : '';
        // 비프리미엄 사용자: 프리미엄 문제 최대 3개로 제한 (JCG-1520)
        // final 모드에서는 이 제한을 적용하지 않음 (파이널 풀 전체가 대상)
        const maxPremiumParam = (!isPremiumOrAbove && !premium && !final && !hardcoreParam) ? '&maxPremium=3' : '';
        const excludeBasicParam = excludeBasic ? '&excludeBasic=true' : '';
        let apiUrl: string;

        // 매운감자(고난도) 퀴즈는 일반 scope 분포와 분리된 전체 이론/코딩 풀에서 조회한다(JCG-3260).
        if (minDifficulty && quizClass) {
          const unsolvedOnlyParam = unsolvedOnly ? '&unsolvedOnly=true' : '';
          apiUrl = `/api/random-problems?count=${count}&minDifficulty=${encodeURIComponent(minDifficulty)}&quizClass=${quizClass}${unsolvedOnlyParam}${excludeBasicParam}&cacheBuster=${cacheBuster}`;
        // tags prop이 있으면 태그 기반 조회
        } else if (tags && tags.length > 0) {
          apiUrl = `/api/random-problems?tags=${encodeURIComponent(JSON.stringify(tags))}&count=${count}${tagMode ? `&tagMode=${tagMode}` : ''}${language ? `&language=${language}` : ''}${premiumParam}${finalParam}${finalSessionParam}${maxPremiumParam}${excludeBasicParam}&cacheBuster=${cacheBuster}`;
        } else {
          const unsolvedOnlyParam = unsolvedOnly ? '&unsolvedOnly=true' : '';
          // 안 푼 문제만 또는 기초 제외로 primary scope 풀이 부족할 때 사용할 fallback scopes.
          const fallbackScopesParam = ((unsolvedOnly || excludeBasic) && fallbackScopes && fallbackScopes.length > 0)
            ? `&fallbackScopes=${encodeURIComponent(JSON.stringify(fallbackScopes))}`
            : '';
          // 단일 API 호출로 모든 scope의 문제를 배치 조회 (N+1 문제 해결)
          apiUrl = `/api/random-problems?scopes=${encodeURIComponent(JSON.stringify(scopes))}${premiumParam}${finalParam}${finalSessionParam}${hardcoreParam}${maxPremiumParam}${unsolvedOnlyParam}${excludeBasicParam}${fallbackScopesParam}&cacheBuster=${cacheBuster}`;
        }

        // excludeIds 전달
        if (allExcludeIds.length > 0) {
          apiUrl += `&excludeIds=${encodeURIComponent(JSON.stringify(allExcludeIds))}`;
        }

        let allProblemIds: string[] = [];
        let fetchFailed = false;
        try {
          const response = await fetchWithRetry(apiUrl, {
            cache: 'no-store',
            signal: requestController.signal,
          });

          if (!response.ok) {
            fetchFailed = true;
            throw new Error(`API 요청 실패: ${response.status}`);
          }

          const result = await response.json();
          if (!isCurrentRequest()) return;

          if (result.success) {
            allProblemIds = result.problemIds;
          } else {
            console.error('문제 배치 조회 실패:', result.error);
          }
        } catch (err) {
          if (!isCurrentRequest()) return;
          fetchFailed = true;
          console.error('문제 배치 조회 실패:', err);
        }

        if (!isCurrentRequest()) return;

        // 안 푼 문제만 모드: API 폴백으로 시도한 문제가 포함될 수 있으므로 클라이언트 필터
        if (unsolvedOnly && attemptedIds.length > 0 && allProblemIds.length > 0) {
          const beforeCount = allProblemIds.length;
          const attemptedSet = new Set(attemptedIds);
          allProblemIds = allProblemIds.filter(id => !attemptedSet.has(id));
          if (process.env.NODE_ENV === 'development') {
            console.log(`[안 푼 문제만] 클라이언트 필터: ${beforeCount}개 → ${allProblemIds.length}개 (${beforeCount - allProblemIds.length}개 제외)`);
            if (beforeCount !== allProblemIds.length) {
              console.log('[안 푼 문제만] API 폴백으로 포함된 시도 문제:', beforeCount - allProblemIds.length, '개');
            }
          }
        }

        // unsolvedOnly 모드에서 결과가 0개: 서버/클라이언트 필터 후 남은 문제 없음
        if (unsolvedOnly && !excludeBasic && allProblemIds.length === 0) {
          setAllSolved(true);
        }

        if (excludeBasic && !fetchFailed) {
          const requestedCount = scopes.length > 0
            ? scopes.reduce((sum, item) => sum + item.count, 0)
            : count;
          if (allProblemIds.length > 0 && allProblemIds.length < requestedCount) {
            setFilterNotice(`기초문제를 제외하고 출제 가능한 문제 ${allProblemIds.length}개를 표시합니다.`);
          }
        }

        if (allProblemIds.length === 0 && (!unsolvedOnly || excludeBasic)) {
          if (fetchFailed) {
            setError("문제를 불러오는데 실패했습니다. 새로고침 후 다시 시도해주세요.");
          } else if (excludeBasic) {
            setError("현재 필터에 맞는 문제가 없습니다. '안 푼 문제만' 또는 '기초문제 제외' 필터를 조정해주세요.");
          } else if (!unsolvedOnly) {
            Sentry.captureMessage("문제를 찾을 수 없음", {
              level: "warning",
              tags: {
                component: "RandomProblems",
                scopes: scopes.map(s => s.scope).join(","),
                totalCount: scopes.reduce((sum, s) => sum + s.count, 0).toString()
              },
              extra: {
                pathname,
                scopes,
                excludedProblemIds: previousProblemIds
              }
            });
            setError("문제를 찾을 수 없습니다.");
          }
        } else if (allProblemIds.length > 0) {
          setProblemIds(allProblemIds);

          // localStorage에 저장 (past prop 사용 시에는 저장하지 않음)
          if (!pastConfig) {
            saveProblemIdsToStorage(storagePath, allProblemIds);
            if (isKeepProblemsActive) {
              saveKeptRandomProblemIds(storagePath, allProblemIds);
            }
            // 새로 출제한 세트는 직전과 문제가 같아도 새 도전이다(JCG-3865). 복원 경로는 이 기록을 이어 쓴다.
            onGamjaExamSetStarted(allProblemIds);
            // 이 세션에서 이 경로를 출제함 → 이후 리사이즈 리마운트 시 복원 대상으로 표시(JCG-2251 보강)
            randomProblemsSessionRegistry.markFetched(storagePath);
          }
        }
      } catch (err) {
        if (!isCurrentRequest()) return;
        setError("문제 목록을 불러오는 중 오류가 발생했습니다.");
        console.error("문제 조회 실패:", err);
      } finally {
        finishCurrentRequest();
      }
  }, [scopes, pastConfig, tags, tagMode, language, minDifficulty, quizClass, count, pathname, storagePath, unsolvedOnly, excludeBasic, fetchAttemptedIds, premium, final, finalSession, hardcore, hardcoreSession, isPremiumOrAbove, fallbackScopes, isKeepProblemsActive, requestGate, onGamjaExamSetStarted]);

  const handleKeepProblemsToggle = React.useCallback((checked: boolean) => {
    setKeepProblemsEnabled(checked);
    saveRandomProblemsKeepMode(checked);

    if (checked) {
      // 화면에 표시 중인 순서(셔플 반영)를 그대로 잠근다. 아직 보고 전이면(셔플 진행 중 등) problemIds 폴백.
      const displayedIds = displayedProblemIdsRef.current;
      const idsToKeep =
        displayedIds.length === problemIds.length ? displayedIds : problemIds;

      if (idsToKeep.length > 0) {
        if (!areStringArraysEqual(problemIds, idsToKeep)) {
          setProblemIds(idsToKeep);
        }
        saveKeptRandomProblemIds(storagePath, idsToKeep);
        refreshProblemAnswerDraftsForPath(pathname, idsToKeep);
        refreshProblemAnswerSessionStatesForPath(pathname, idsToKeep);
      }
      return;
    }

    clearKeptRandomProblemIds(storagePath);
    clearProblemAnswerDraftsForPath(pathname);
    clearProblemAnswerSessionStatesForPath(pathname);
  }, [problemIds, pathname, storagePath]);

  useEffect(() => {
    if (!isKeepProblemsActive || problemIds.length === 0) return;
    const keptState = getKeptRandomProblemIdsState(storagePath);

    if (keptState && areStringArraysEqual(keptState.problemIds, problemIds)) {
      return;
    }

    saveKeptRandomProblemIds(storagePath, problemIds);
  }, [isKeepProblemsActive, storagePath, problemIds]);

  const handleProblemOrderChange = React.useCallback((orderedProblemIds: string[]) => {
    // ref 만 갱신(리렌더 불필요). 토글 핸들러가 이 최신 순서를 동기적으로 읽는다.
    displayedProblemIdsRef.current = orderedProblemIds;
  }, []);

  const handleKeepProblemsExtend = React.useCallback((): number | null => {
    const displayedIds = displayedProblemIdsRef.current;
    const idsToKeep =
      displayedIds.length === problemIds.length ? displayedIds : problemIds;

    if (idsToKeep.length === 0) return null;

    if (!areStringArraysEqual(problemIds, idsToKeep)) {
      setProblemIds(idsToKeep);
    }

    const expiresAt = saveKeptRandomProblemIds(storagePath, idsToKeep);
    refreshProblemAnswerDraftsForPath(pathname, idsToKeep);
    refreshProblemAnswerSessionStatesForPath(pathname, idsToKeep);
    return expiresAt;
  }, [pathname, storagePath, problemIds]);

  // 안 푼 문제만 토글 핸들러
  const handleUnsolvedOnlyToggle = React.useCallback((checked: boolean) => {
    const nextUnsolvedOnly = isPassLoaded && isPremiumOrAbove && checked;
    setStoredUnsolvedOnly(nextUnsolvedOnly);
    try {
      localStorage.setItem(UNSOLVED_ONLY_STORAGE_KEY, String(nextUnsolvedOnly));
    } catch {
      // localStorage 접근 불가 시 무시
    }
    // 토글 변경 시 문제 재선택. 단 problemIds/isLoading 을 비우지 않아 기존 문제를 유지한 채
    // 재요청(stale-while-revalidate) — 사이드바 컨트롤(점수·문제목록·보기설정)이 풀스크린
    // 스켈레톤으로 언마운트돼 사라지는 현상 방지(JCG-2066). unsolvedOnly 변경이 effect 를 재실행.
    hasFetched.current = false;
    setError(null);
    setAllSolved(false);
  }, [isPassLoaded, isPremiumOrAbove]);

  const handleExcludeBasicToggle = React.useCallback((checked: boolean) => {
    requestGate.abort();
    // 이전 설정의 세트가 keep·리마운트 경로에서 다시 나타나지 않게 한다.
    isInitialMount.current = false;
    clearKeptRandomProblemIds(storagePath);
    setStoredExcludeBasic(checked);
    try {
      localStorage.setItem(EXCLUDE_BASIC_STORAGE_KEY, String(checked));
      localStorage.removeItem(getStorageKey(storagePath));
    } catch {
      // localStorage 접근 불가 시 현재 화면의 설정만 적용
    }
    hasFetched.current = false;
    setError(null);
    setAllSolved(false);
    setFilterNotice(null);
  }, [requestGate, storagePath]);

  // 다시 풀기 핸들러
  const handleRetry = () => {
    // 페이지 최상단으로 스크롤
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // 안 푼 문제 토글 등으로 진행 중인 요청이 늦게 완료되어 hasFetched/keep 상태를
    // 다시 덮어쓰지 못하게 명시적 재출제 전에 동기 취소한다(JCG-3246).
    requestGate.abort();

    // 문제 재선택을 위해 hasFetched 리셋
    hasFetched.current = false;

    // 상태 초기화
    clearKeptRandomProblemIds(storagePath);
    clearProblemAnswerDraftsForPath(pathname);
    clearProblemAnswerSessionStatesForPath(pathname);
    setProblemIds([]);
    setIsLoading(true);
    setError(null);
    // 백그라운드 복원 flag 를 내려 새로 출제되는 문제에 복원 경로가 잔류하지 않게 한다(JCG-3222).
    setDidBackgroundRestore(false);

    if (onRetry) {
      onRetry();
      return;
    }

    // 부모에서 범위를 관리하지 않는 RandomProblems는 기존 범위로 즉시 재조회한다.
    fetchProblems();
  };

  // "다시 풀기" 진입점 (JCG-2527 가드, JCG-2510 reveal 패턴 미러링).
  // - "다시 보지 않기"로 dismiss 한 경우: 팝업 없이 바로 handleRetry
  // - 그 외: 확인 다이얼로그만 열고 실제 초기화는 보류
  const requestRetry = () => {
    if (isConfirmDismissed(RETRY_CONFIRM_DISMISSED_KEY)) {
      handleRetry();
      return;
    }

    setShowRetryConfirm(true);
  };

  // 확인 다이얼로그에서 "다시 풀기"를 승인한 경우에만 초기화한다.
  const confirmRetry = () => {
    setShowRetryConfirm(false);
    handleRetry();
  };

  // 확인 다이얼로그 취소/바깥 클릭: 초기화하지 않고 닫기만.
  const cancelRetry = () => {
    setShowRetryConfirm(false);
  };

  // 부모에서 호출 가능한 reset 함수 (JCG-1430)
  // localStorage를 완전히 비우고 페이월 차감 없이 재출제
  // 프리미엄 모의고사 단일 페이지 등에서 "다시 풀기"를 커스텀 UI로 제공할 때 사용
  const handleResetSample = React.useCallback(() => {
    // localStorage 비우기
    try {
      localStorage.removeItem(getStorageKey(storagePath));
    } catch (err) {
      console.error("localStorage 제거 실패:", err);
    }
    clearKeptRandomProblemIds(storagePath);
    clearProblemAnswerDraftsForPath(pathname);
    clearProblemAnswerSessionStatesForPath(pathname);

    // 재출제를 위해 hasFetched 리셋
    hasFetched.current = false;

    // 상태 초기화 → useEffect가 fetchProblems 재실행
    setProblemIds([]);
    setIsLoading(true);
    setError(null);
    setAllSolved(false);
    // 백그라운드 복원 flag 를 내려 새로 출제되는 문제에 복원 경로가 잔류하지 않게 한다(JCG-3222).
    setDidBackgroundRestore(false);

    fetchProblems();
  }, [pathname, storagePath, fetchProblems]);

  React.useImperativeHandle(ref, () => ({
    reset: handleResetSample,
  }), [handleResetSample]);

  useEffect(() => {
    // 현재 라우트가 아니면 보존된 Next page 트리에서 요청을 시작하지 않는다.
    if (!isRouteActive) {
      requestGate.abort();
      return;
    }

    // 첫 요청에 저장된 필터와 이용권을 함께 반영한다. 로딩 중 free 판정으로 출제하지 않는다.
    if (!isPassLoaded || storedUnsolvedOnly === null || storedExcludeBasic === null || !hasLoadedKeepMode) return;

    // 체험 종료 등으로 유효 필터가 바뀌면 완료된 출제와 진행 중인 요청을 모두 무효화한다.
    if (
      discardFilteredProblemsRef.current ||
      (appliedUnsolvedOnlyRef.current !== null &&
        appliedUnsolvedOnlyRef.current !== unsolvedOnly) ||
      (appliedExcludeBasicRef.current !== null &&
        appliedExcludeBasicRef.current !== excludeBasic)
    ) {
      requestGate.abort();
      hasFetched.current = false;
      setAllSolved(false);
      setDidBackgroundRestore(false);
    }
    if (discardFilteredProblemsRef.current) {
      // 필터로 줄어든 구 세트는 keep·리마운트·백그라운드 복원에서도 재사용하지 않는다.
      isInitialMount.current = false;
      clearKeptRandomProblemIds(storagePath);
      clearPathnameHidden(pathname);
      try {
        localStorage.removeItem(getStorageKey(storagePath));
      } catch {
        // localStorage 접근 불가 시 무시
      }
      discardFilteredProblemsRef.current = false;
    }
    appliedUnsolvedOnlyRef.current = unsolvedOnly;
    appliedExcludeBasicRef.current = excludeBasic;

    // Next가 이전 page 상태를 그대로 복원해도, 다른 pathname을 다녀온 비-keep visit은
    // 반드시 fresh fetch로 전환한다. keep은 유료 1시간 보존 계약이 우선이므로 현재 상태를 유지한다.
    const isNewRouteVisit = handledRouteRevisionRef.current !== routeRevision;
    handledRouteRevisionRef.current = routeRevision;
    if (isNewRouteVisit && !isKeepProblemsActive) {
      hasFetched.current = false;
      setProblemIds([]);
      setIsLoading(true);
      setError(null);
      setAllSolved(false);
      setDidBackgroundRestore(false);
    }

    // 이미 데이터를 가져왔다면 다시 가져오지 않음
    if (hasFetched.current) {
      return;
    }

    // unsolvedOnly가 켜져있으면 Clerk 로드 완료까지 대기
    if (unsolvedOnly && !isUserLoaded) {
      return;
    }

    // 문제 유지가 켜져 있으면 실제 리로드 후에도 1시간 안의 문제 세트를 복원한다(JCG-2448).
    // 꺼져 있으면 아래 JCG-2251 세션 리마운트 복원만 허용해 기존 "새로고침 시 새 문제" 동작을 보존한다.
    const wasInitialMount = isInitialMount.current;
    isInitialMount.current = false;
    if (
      wasInitialMount &&
      isKeepProblemsActive
    ) {
      const keptState = getKeptRandomProblemIdsState(storagePath);
      if (keptState) {
        hasFetched.current = true;
        randomProblemsSessionRegistry.markFetched(storagePath);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setProblemIds(keptState.problemIds);
        setIsLoading(false);
        return;
      }
    }

    // 리사이즈로 인한 LayoutSwitch 리마운트일 때만 직전 세트를 복원해 새 문제 재추첨을 막는다(JCG-2251).
    // 복원 조건을 "최초 마운트 + 이 JS 세션에서 이미 출제한 경로"로 제한한다 →
    // F5/페이지 리로드는 모듈이 새로 평가돼 레지스트리가 비어 있어 새 문제를 출제하므로
    // "새로고침 시 새 문제"(JCG-283) 동작이 보존된다.
    // SPA로 다른 페이지를 다녀오면 LayoutSwitch route commit이 표시를 지워
    // 재진입도 새 문제를 출제한다(JCG-3044).
    // 마운트 직후 한 번만 동작하므로 "다시 풀기"/reset(마운트 이후 트리거)에는 영향 없다.
    if (
      wasInitialMount &&
      randomProblemsSessionRegistry.hasFetched(storagePath)
    ) {
      const previousProblemIds = getProblemIdsFromStorage(storagePath);
      if (previousProblemIds && previousProblemIds.length > 0) {
        hasFetched.current = true;
        setProblemIds(previousProblemIds);
        setIsLoading(false);
        return;
      }
    }

    // iOS Safari 백그라운드 복귀 리로드 복원 (JCG-2895).
    // 탭이 hidden 된 뒤 경과 시간(away)이 [MIN_AWAY_MS, MAX_AWAY_MS] 이면 능동 새로고침이 아니라
    // 백그라운드에 오래 있다가 다시 로드된 것(= Safari 자동 리로드)으로 보고 직전 세트를 복원한다.
    // 능동 F5 는 언로드→재로드가 거의 즉시라 away 가 MIN_AWAY_MS 미만이므로 새 문제가 나온다(JCG-283 보존).
    // 문제유지(keep) 사용자는 위 kept-state 복원이 우선하므로 여기선 비-keep 경로만 처리한다.
    // 마커는 초기 마운트에서 판정 여부와 무관하게 즉시 삭제(one-shot)해, 복귀 직후 의도적 F5 에는 새 문제가 나온다.
    if (wasInitialMount && !isKeepProblemsActive && !pastConfig) {
      const awayMs = getAwayMsSinceHidden(pathname);
      clearPathnameHidden(pathname);
      if (awayMs !== null && awayMs >= MIN_AWAY_MS && awayMs <= MAX_AWAY_MS) {
        const previousProblemIds = getProblemIdsFromStorage(storagePath);
        if (previousProblemIds && previousProblemIds.length > 0) {
          hasFetched.current = true;
          randomProblemsSessionRegistry.markFetched(storagePath);
          // 되살린 채점 상태를 이 JS 세션의 runtime 키로 등록한다(keep 토글과 같은 경로). didBackgroundRestore 는
          // 리사이즈 리마운트에서 false 로 돌아가므로, 이 등록이 없으면 좌측 목록·점수가 리로드 전 채점을 놓친다(JCG-3855).
          refreshProblemAnswerSessionStatesForPath(pathname, previousProblemIds);
          // 세트가 동일하므로 입력한 답/풀이 상태와 표시 순서도 복원되도록 켠다(ProblemAnswerFetcher·RandomizedProblems).
          setDidBackgroundRestore(true);
          setProblemIds(previousProblemIds);
          setIsLoading(false);
          return;
        }
      }
    }

    fetchProblems();
  }, [fetchProblems, unsolvedOnly, storedUnsolvedOnly, excludeBasic, storedExcludeBasic, isUserLoaded, pastConfig, pathname, storagePath, isKeepProblemsActive, hasLoadedKeepMode, isPassLoaded, isRouteActive, requestGate, routeRevision]);

  // 탭 가시성 변화를 마커에 기록한다 (JCG-2895).
  // - hidden(백그라운드 전환/언로드): 그 시각을 저장 → 위 마운트 로직이 away-duration 으로 백그라운드
  //   복귀 여부를 판정한다. 능동 F5 는 away 가 짧아 자연히 걸러진다.
  // - visible(리로드 없이 복귀): 마커를 지운다 → 이후 능동 F5 가 낡은 hidden 시각을 오판하지 않게 한다.
  // 마커 저장만으로는 어떤 동작도 하지 않는다. past 모드는 리로드해도 같은 회차라 복원이 불필요하다.
  // deps 는 pastConfig(memo 객체) 대신 isPastMode(boolean)를 써서 부모 리렌더로 인한 리스너 churn 을 피한다.
  useEffect(() => {
    if (isPastMode) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        markPathnameHidden(pathname);
      } else {
        clearPathnameHidden(pathname);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pathname, isPastMode]);

  // 필터 UI 내부 (래퍼 없이 — RandomizedProblems headerSlot으로 전달 시 같은 줄 배치)
  // "전체 보기 숨기기"와 동일한 토글 버튼 형식(JCG-2066).
  const keepProblemsButton = canKeepProblems ? (
    <Button
      onClick={() => handleKeepProblemsToggle(!keepProblemsEnabled)}
      variant="outline"
      size="sm"
      aria-pressed={keepProblemsEnabled}
      className={`${outlineToggleClass(keepProblemsEnabled)} !w-full`}
    >
      <Pin className="h-4 w-4 mr-1" aria-hidden="true" />
      문제유지(1시간)
    </Button>
  ) : null;

  const keepProblemsActiveControls = isKeepProblemsActive ? (
    <KeepProblemsActiveControls
      key={storagePath}
      readRemaining={() => getRemainingKeepMsForPath(storagePath)}
      hasProblemIds={problemIds.length > 0}
      onExtend={handleKeepProblemsExtend}
    />
  ) : null;

  const keepProblemsControl = keepProblemsButton ? (
    <div className="flex w-full flex-col gap-1 rounded-lg border border-border bg-muted/40 p-1 @[28rem]/quiz-filters:w-auto">
      {keepProblemsButton}
      {keepProblemsActiveControls}
    </div>
  ) : null;

  const unsolvedOnlyButton = isPremiumOrAbove ? (
    <Button
      onClick={() => handleUnsolvedOnlyToggle(!unsolvedOnly)}
      variant="outline"
      size="sm"
      aria-pressed={unsolvedOnly}
      className={`${outlineToggleClass(unsolvedOnly)} min-w-0 w-full justify-center px-1`}
    >
      <Filter className="mr-1 h-4 w-4 shrink-0" aria-hidden="true" />
      안 푼 문제만
    </Button>
  ) : null;

  const excludeBasicButton = allowExcludeBasic ? (
    <Button
      onClick={() => handleExcludeBasicToggle(!excludeBasic)}
      variant="outline"
      size="sm"
      aria-pressed={excludeBasic}
      className={`${outlineToggleClass(excludeBasic)} min-w-0 w-full justify-center px-1`}
    >
      <Filter className="mr-1 h-4 w-4 shrink-0" aria-hidden="true" />
      기초문제 제외
    </Button>
  ) : null;

  const filterContent = keepProblemsControl || unsolvedOnlyButton || excludeBasicButton ? (
    <div className="flex min-w-0 flex-col gap-2">
      {(unsolvedOnlyButton || excludeBasicButton) && (
        <div className={`grid min-w-0 gap-2 ${unsolvedOnlyButton && excludeBasicButton ? "grid-cols-2" : "grid-cols-1"}`}>
          {unsolvedOnlyButton}
          {excludeBasicButton}
        </div>
      )}
      {keepProblemsControl}
    </div>
  ) : null;

  // 종료 상태(로딩/에러/빈/모두풀이완료)용 필터 컨트롤 — 셸이면 사이드바 portal, 비셸이면 본문 인라인.
  const filterUI = filterContent ? (
    <SidebarFilterControl
      filterContent={filterContent}
      showChoicesToggle={isPremiumOrAbove}
    />
  ) : null;

  const mobileTools = (
    <RandomProblemsMobileTools
      canKeepProblems={canKeepProblems}
      keepProblemsEnabled={keepProblemsEnabled}
      isKeepProblemsActive={isKeepProblemsActive}
      problemIdsLength={problemIds.length}
      pathname={storagePath}
      isPremiumOrAbove={isPremiumOrAbove}
      unsolvedOnly={unsolvedOnly}
      allowExcludeBasic={allowExcludeBasic}
      excludeBasic={excludeBasic}
      onKeepProblemsToggle={handleKeepProblemsToggle}
      onKeepProblemsExtend={handleKeepProblemsExtend}
      onUnsolvedOnlyToggle={handleUnsolvedOnlyToggle}
      onExcludeBasicToggle={handleExcludeBasicToggle}
    />
  );

  const randomProblemItems = React.useMemo(
    () =>
      problemIds.map((id: string) => (
        <ProblemAnswerFetcher
          key={id}
          id={id}
          persistDraft
          // 문제유지(keep) 또는 백그라운드 복귀 복원(JCG-2895)이면 리로드 시 draft/풀이 상태 유지·복원
          persistDraftAcrossReload={restoreDraftAndResults}
          revealWithoutGradingNotice={
            gamjaExam.slug
              ? "감자시험 도전 횟수를 기록하려면 모든 문제를 반드시 풀어야 해요. 정답이 생각나지 않아도 답을 입력해 제출해 보세요."
              : undefined
          }
        />
      )),
    [problemIds, restoreDraftAndResults, gamjaExam.slug],
  );

  // 풀스크린 스켈레톤은 "최초 로딩(아직 문제 없음)"에만. 재요청(안 푼 문제만 토글 등)에는
  // 기존 problemIds 를 유지해 RandomizedProblems(사이드바 컨트롤 포함)를 언마운트하지 않는다(JCG-2066).
  // 상태별 본문(body). 컨트롤(SidebarFilterControl)은 아래에서 모든 상태에 걸쳐 지속 렌더되므로,
  // 각 분기는 본문만 만든다 — 종료 상태에서도 보기 설정 컨트롤이 사라지지 않는다(JCG-2066).
  let body: React.ReactNode;
  if (isLoading && problemIds.length === 0) {
    // past prop이 있으면 스켈레톤 개수를 예측할 수 없으므로 기본값 사용
    const totalCount = pastConfig
      ? 10
      : scopes.length > 0
        ? scopes.reduce((sum, { count }) => sum + count, 0)
        : count;
    body = (
      <div className="space-y-4">
        {Array.from({ length: totalCount }).map((_, index) => (
          <div key={index} className="space-y-3">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = (
      <div className="my-10 overflow-hidden rounded-lg border border-red-200 bg-red-50 dark:border-red-700 dark:bg-red-900/20">
        <div className="p-6 text-center">
          <p className="text-red-600 dark:text-red-400 font-medium">오류</p>
          <p className="text-red-500 dark:text-red-300 mt-2">{error}</p>
        </div>
      </div>
    );
  } else if (allSolved) {
    body = (
      <div className="my-10 overflow-hidden rounded-lg border border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/20">
        <div className="flex flex-col items-center p-6 text-center">
          <Image
            src="/exams/3d_finger_guns_gamja.webp"
            alt="모든 문제를 푼 걸 축하하는 감자"
            width={160}
            height={160}
            className="mb-3 h-32 w-32"
          />
          <p className="text-green-600 dark:text-green-400 font-medium">
            모든 문제를 풀었어요!
          </p>
          {/* allSolved 는 unsolvedOnly(프리미엄 이상)에서만 true 이므로 필터 끄기 안내가 항상 유효(JCG-2557).
              버튼은 사이드바/모바일의 "안 푼 문제만" 토글과 같은 핸들러를 호출해 필터를 끈다 — 별도 동작이 아님을
              분명히 하려고 라벨·아이콘을 그 토글과 맞췄다. 끄면 전체 문제가 다시 출제된다. */}
          <p className="mt-2 text-sm text-green-700 dark:text-green-300">
            ‘안 푼 문제만’ 필터가 켜져 있어요. 필터를 끄면 전체 문제를 다시 풀 수 있어요.
          </p>
          <Button
            onClick={() => handleUnsolvedOnlyToggle(false)}
            size="lg"
            className="mt-4"
          >
            <Filter className="mr-2 h-4 w-4" aria-hidden="true" />
            안 푼 문제만 끄기
          </Button>
        </div>
      </div>
    );
  } else if (problemIds.length === 0) {
    // 프리미엄 풀이 비어 있는 경우 (premium prop 부착된 문제가 0개) — JCG-1430
    // 파이널 풀이 비어 있는 경우 (_final_ id 문제가 0개) — JCG-1675
    const premiumSuffix = final
      ? " (파이널 모의고사 문제 풀이 준비 중이거나 비어 있습니다)"
      : hardcore
        ? " (하드코어 11 문제가 준비 중이거나 비어 있습니다)"
        : premium
          ? " (프리미엄 문제 풀이 준비 중이거나 비어 있습니다)"
          : "";
    const message = pastConfig
      ? `${pastConfig.examYear}년 ${pastConfig.examRound}회 문제를 찾을 수 없습니다.${premiumSuffix}`
      : scopes.length > 0
        ? `${scopes.map(({ scope }) => scope).join(", ")} 문제를 찾을 수 없습니다.${premiumSuffix}`
        : `문제를 찾을 수 없습니다.${premiumSuffix}`;
    body = (
      <div className="my-10 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">{message}</p>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        <RandomizedProblems
          suppressControls
          shuffle={!isKeepProblemsActive}
          onProblemOrderChange={handleProblemOrderChange}
          // 좌측 목록/점수(ExamScore)를 본문 draft와 같은 조건으로 복원한다(JCG-3222 비대칭 방지, restoreDraftAndResults SSOT).
          restoreProblemResultsAcrossReload={restoreDraftAndResults}
          // 백그라운드 복귀 리로드에서 직전 표시 순서까지 복원한다. 파이널 회차별로 저장을 분리한다(JCG-3510).
          orderStorageKey={storagePath}
          restoreOrderAcrossReload={restoreDraftAndResults}
          scoreTitle={examScoreTitle}
          onAllProblemsScored={gamjaExam.onAllProblemsScored}
        >
          {randomProblemItems}
        </RandomizedProblems>
        <div className="my-8 flex flex-col items-center gap-2">
          {gamjaExam.slug && (
            <GamjaExamChallengeResult completedCount={gamjaExam.completedCount} />
          )}
          <Button onClick={requestRetry} variant="destructive" size="lg">
            <RotateCcw className="mr-2 h-4 w-4" />
            다시 풀기
          </Button>
        </div>
      </>
    );
  }

  // 전체 출력을 ChoicesVisibilityProvider 로 감싼다 — 종료 상태에서도 "전체 보기 숨기기" 토글이
  // 동작하도록(컨텍스트가 RandomizedProblems 안이 아니라 여기서 제공됨). RandomizedProblems 는
  // 부모 provider 를 재사용한다(중첩 방지). 컨트롤은 SidebarFilterControl 이 모든 상태에서 portal.
  const quizContent = (
    <ChoicesVisibilityProvider>
      <div data-llm-copy-disable-page="true" data-llm-copy-skip="true">
        {filterUI}
        {mobileTools}
        {filterNotice && (
          <p role="status" className="mb-4 text-sm text-muted-foreground">
            {filterNotice}
          </p>
        )}
        {gamjaExam.slug && <GamjaExamChallengeSummary slug={gamjaExam.slug} />}
        {body}

        {/* "다시 풀기" 확인 다이얼로그 (JCG-2527): 버튼을 실수로 눌러 풀이 기록이
            초기화되는 불편 해소. JCG-2510 reveal 확인 다이얼로그 패턴 미러링. */}
        <Dialog
          open={showRetryConfirm}
          onOpenChange={(open) => {
            if (!open) cancelRetry();
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>다시 풀까요?</DialogTitle>
              <DialogDescription>
                현재 풀이 기록이 초기화되고 새 문제 {problemIds.length}개가 출제됩니다.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cancelRetry}>
                취소
              </Button>
              <Button variant="destructive" onClick={confirmRetry}>
                다시 풀기
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ChoicesVisibilityProvider>
  );

  return (
    <ProblemRenderDiagnosticsBoundary pathname={pathname} problemIds={problemIds}>
      {quizContent}
    </ProblemRenderDiagnosticsBoundary>
  );
});

RandomProblems.displayName = "RandomProblems";

export default RandomProblems;
